// CLE-2914: the Claims list keeps its show/hide column choice in localStorage (CLE-2512). A stored
// array never gains a column added later, so a user who had ever touched the picker would never
// see a new column. Each entry below is a one-time migration: it inserts `key` after `after` in the
// stored array (appending when `after` is not there) and is then recorded in a separate marker
// array. Keying on the marker, not on the column's absence, is what lets a user untick the column
// afterwards and have it stay unticked.

export const CLAIMS_VISIBLE_COLUMNS_STORAGE_KEY =
  "clearhealth_claims_visible_columns";
export const CLAIMS_VISIBLE_COLUMNS_MIGRATIONS_STORAGE_KEY =
  "clearhealth_claims_visible_columns_migrations";

export interface IVisibleColumnMigration {
  // The column key to add; also the id recorded in the marker array.
  key: string;
  // The column it goes after; appended when that column is not in the stored array.
  after: string;
}

export const CLAIMS_VISIBLE_COLUMN_MIGRATIONS: IVisibleColumnMigration[] = [
  { key: "medicareExpectedAmount", after: "expectedAmount" },
];

export interface IVisibleColumnsMigrationResult {
  // The stored array after the migrations, or null when nothing was stored.
  columns: string[] | null;
  // The marker array after the migrations: every migration id, applied or not needed.
  appliedMigrations: string[];
  // True only when `columns` differs from what was stored and must be written back.
  columnsChanged: boolean;
}

/**
 * Pure: applies every migration not yet in `appliedMigrations` to the stored column array.
 *
 * With nothing stored, the list falls back to its defaults, which already include every new
 * column, so the migrations are only marked — otherwise a user who later unticks the column
 * would have it put back on the next visit.
 */
export function migrateVisibleColumns(
  storedColumns: string[] | null,
  appliedMigrations: string[],
  migrations: IVisibleColumnMigration[] = CLAIMS_VISIBLE_COLUMN_MIGRATIONS
): IVisibleColumnsMigrationResult {
  const applied = [...appliedMigrations];
  let columns = storedColumns ? [...storedColumns] : null;
  let columnsChanged = false;

  migrations.forEach(({ key, after }) => {
    if (applied.includes(key)) return;
    applied.push(key);

    if (!columns || columns.includes(key)) return;
    const anchorIndex = columns.indexOf(after);
    columns =
      anchorIndex === -1
        ? [...columns, key]
        : [
            ...columns.slice(0, anchorIndex + 1),
            key,
            ...columns.slice(anchorIndex + 1),
          ];
    columnsChanged = true;
  });

  return { columns, appliedMigrations: applied, columnsChanged };
}

// A value that is not an array of strings is treated as absent: the list's hook falls back to
// its defaults for unreadable JSON too, and the defaults already carry every new column.
const readStringArray = (storage: Storage, key: string): string[] | null => {
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) &&
      parsed.every((item) => typeof item === "string")
      ? parsed
      : null;
  } catch {
    return null;
  }
};

/**
 * Reads the stored column array and marker, applies the pending migrations and writes both back.
 * Must run before `useLocalStorageState` reads the column key. Idempotent, and never throws: a
 * blocked or full localStorage leaves the list on whatever it would have shown anyway.
 */
export function runClaimsVisibleColumnsMigrations(
  storage: Storage = window.localStorage
): void {
  try {
    const appliedMigrations =
      readStringArray(storage, CLAIMS_VISIBLE_COLUMNS_MIGRATIONS_STORAGE_KEY) ??
      [];
    const pending = CLAIMS_VISIBLE_COLUMN_MIGRATIONS.some(
      ({ key }) => !appliedMigrations.includes(key)
    );
    if (!pending) return;

    const result = migrateVisibleColumns(
      readStringArray(storage, CLAIMS_VISIBLE_COLUMNS_STORAGE_KEY),
      appliedMigrations
    );
    if (result.columnsChanged && result.columns) {
      storage.setItem(
        CLAIMS_VISIBLE_COLUMNS_STORAGE_KEY,
        JSON.stringify(result.columns)
      );
    }
    storage.setItem(
      CLAIMS_VISIBLE_COLUMNS_MIGRATIONS_STORAGE_KEY,
      JSON.stringify(result.appliedMigrations)
    );
  } catch (error) {
    console.warn("Could not migrate the claims list visible columns:", error);
  }
}
